<script setup>
  import { computed, useTemplateRef, ref } from 'vue';
  import {
    DocumentTextIcon,
    PhotoIcon,
    VideoCameraIcon,
    DocumentIcon,
    TrashIcon,
    ArchiveBoxXMarkIcon,
  } from '@heroicons/vue/24/outline';

  import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
  import { useDraggable } from '@vueuse/core';

  const editName = ref(false);

  function toggleEditName() {
    editName.value = !editName.value;
  }

  const props = defineProps({
    asset: {
      type: Object,
      required: true
    },
    willBeDeleted: {
      type: Boolean,
      default: false
    }
  });

  const emit = defineEmits(['remove-asset','drag-start','drag-end','drag-move']);

  const isNew = computed(() => !!props.asset.file);

  const mediaAsset = computed(() => {
    return props.asset.file || props.asset;
  });

  const mimeType = computed(() => {
    return mediaAsset.value.mime_type || mediaAsset.value.type || '';
  });

  const fileType = computed(() => {
    if (mimeType.value.includes('image')) {
      return 'Image';
    } else if (mimeType.value.includes('video')) {
      return 'Video';
    } else if (mimeType.value.includes('doc')) {
      return 'Word Document';
    } else if (mimeType.value.includes('text')) {
      return 'Text File';
    } else if (mimeType.value.includes('pdf')) {
      return 'PDF';
    } else {
      return 'File';
    }
  });

  const el = useTemplateRef('el');
  const dragHandle = useTemplateRef('dragHandle');
  const draggableWidth = ref(0);
  const dropTargetGroupId = ref(null);
  const previousClientY = ref(null);

  const {x, y, isDragging, style} = useDraggable(el, {
    stopPropagation:true,
    preventDefault: true,
    handle: dragHandle,
    onStart: (position, e) => {
      draggableWidth.value = el.value.getBoundingClientRect().width;
      emit('drag-start', props.asset);
    },
    onMove: (position, e) => {
      const main = document.querySelector('main');

      if(
        e.clientY < 100 &&
        previousClientY.value &&
        e.clientY < previousClientY.value
      ){
        main.scrollTo({
          top: main.scrollTop - 400,
          behavior: 'smooth'
        });
      } else if(
        e.clientY > e.view.innerHeight - 50 &&
        previousClientY.value &&
        e.clientY > previousClientY.value
      ){
        main.scrollTo({
          top: main.scrollTop + 400,
          behavior: 'smooth'
        });
      }

      const dropTarget = checkDropZones(e.clientX, e.clientY);
      dropTargetGroupId.value = dropTarget
      emit('drag-move', props.asset, dropTargetGroupId.value);
      previousClientY.value = e.clientY;
    },
    onEnd: (position, e) => {
      position.x = 0
      position.y = 0
      if(typeof dropTargetGroupId.value !== 'undefined') {
        props.asset.group_id = dropTargetGroupId.value;
      }
      emit('drag-move', props.asset, undefined);
    }
  });

  function checkDropZones(mouseX, mouseY) {
    const elements = document.elementsFromPoint(mouseX, mouseY);

    for (const element of elements) {
      const dropZone = element.closest('[data-group-id]')
      if (dropZone) {
        const groupId = dropZone.getAttribute('data-group-id');
        if(groupId === 'null') return null;
        return groupId;
      }
    }
    return;
  }
</script>

<template>
  <div
    ref="el"
    class="flex shadow-sm rounded-md mb-2 relative"
    :style="{
      top: `${y}px`,
      left: `${x}px`,
      position: isDragging ? 'fixed' : 'relative',
      zIndex: isDragging ? 20000 : '',
      width: isDragging ? `${draggableWidth}px` : '100%',
      pointerEvents: isDragging ? 'none' : 'auto',
    }"
  >
    <a
      :href="asset.url"
      target="_blank"
      download
    >
      <div class="relative shrink-0 flex items-center justify-center w-14 h-14 bg-indigo-600 text-white text-sm leading-5 font-medium rounded-l-md">
        <TrashIcon
          v-if="mediaAsset.willBeDeleted"
          class="absolute text-red-600 w-full h-full"
        />
        <img
          class="rounded-l-md"
          v-if="mediaAsset.thumbnail || mediaAsset.preview_url"
          :src="mediaAsset.thumbnail || mediaAsset.preview_url"
          alt="thumbnail"
        >
        <div v-else class="text-indigo-100 w-full">
          <PhotoIcon v-if="fileType === 'Image'" class="w-10 h-10 m-auto" />
          <DocumentTextIcon v-else-if="fileType === 'Text File'" class="w-10 h-10 m-auto" />
          <DocumentIcon v-else-if="fileType === 'Word Document'" class="w-10 h-10 m-auto" />
          <VideoCameraIcon v-else-if="fileType === 'Video'" class="w-10 h-10 m-auto" />
          <DocumentTextIcon v-else-if="fileType === 'PDF'" class="w-10 h-10 m-auto" />
          <DocumentIcon v-else class="w-10 h-10 m-auto" />
        </div>
      </div>
    </a>
    <div class="flex-1 flex items-center justify-between border-t border-r border-b border-gray-200 bg-white rounded-r-md truncate">
      <div class="ml-2">
        <FontAwesomeIcon
          v-if="!asset.willBeDeleted"
          ref="dragHandle"
          :icon="['fas', 'arrows-up-down-left-right']"
          class="inline h-4 mr-2 cursor-move text-gray-600"
          title="Drag to move groups"
        />
        <FontAwesomeIcon
          v-if="!asset.willBeDeleted"
          :icon="['fas', 'sort']"
          class="inline h-4 cursor-grab asset-sort-handle text-gray-600"
          title="Drag to reorder"
        />
      </div>

      <div
        class="flex-1 px-2 py-2 text-sm leading-5 truncate"
        :title="asset.name || mediaAsset.name"
      >
        <span v-if="!editName">
          <FontAwesomeIcon
            @click="toggleEditName"
            :icon="['fas', 'fa-pencil']"
            class="inline h-3 cursor-pointer text-gray-500 hover:text-gray-900"
            title="Edit Name"
          />
          {{ asset.name || mediaAsset.name}}
        </span>

        <input
          v-if="asset.name && !asset.willBeDeleted"
          @keyup.enter.stop.prevent="toggleEditName"
          :type="editName ? 'text' : 'hidden'"
          v-model="asset.name"
          :name="
            asset.created_at
              ? `existingMedia[${asset.id}][name]`
              : ''
            "
          class="w-full p-1 border border-gray-300 rounded text-xs"
        >
        <span
          v-if="isNew"
          class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium leading-4 bg-blue-100 text-blue-800"
        >
          New
        </span>
        <span
          v-else-if="asset.willBeDeleted"
          class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium leading-4 bg-red-100 text-red-800"
        >
          To Be Deleted
        </span>

        <p class="text-gray-500">
          {{fileType}}
        </p>
      </div>

      <div class="shrink-0 pr-2">
        <div>
          <button
            v-if="asset.willBeDeleted"
            @click.prevent.stop="emit('restore-asset', asset)"
            type="button"
            title="Restore"
            dusk="restore-media"
            class="inline-flex items-center justify-center text-blue-400 hover:text-blue-600 rounded-full bg-transparent focus:outline-none focus:text-blue-500 focus:bg-blue-100 transition ease-in-out duration-150"
          >
            <FontAwesomeIcon
              :icon="['fal', 'trash-undo']"
              class="inline h-4 ml-2 text-blue-400"
              title="Drag to reorder"
            />
          </button>
          <button
            v-else
            @click.prevent.stop="emit('remove-asset', asset)"
            type="button"
            title="Delete"
            dusk="remove-media"
            class="h-4 inline-flex items-center justify-center text-red-400 rounded-full bg-transparent hover:text-red-900 focus:outline-none focus:text-red-500 focus:bg-red-100 transition ease-in-out duration-150"
          >
            <FontAwesomeIcon
              :icon="['fal', 'trash-xmark']"
              class="inline h-4 ml-2 text-red-600"
              title="Drag to reorder"
            />
          </button>
        </div>
      </div>
    </div>

    <div v-if="asset.created_at">
      <input
        v-if="asset.id"
        type="hidden"
        :value="asset.id"
        :name="
          asset.willBeDeleted
            ? `deletedMedia[${asset.id}][id]`
            : `existingMedia[${asset.id}][id]`
          "
      >
      <input
        v-if="typeof asset.order !== 'undefined' && !asset.willBeDeleted"
        type="hidden"
        :value="asset.order"
        :name="`existingMedia[${asset.id}][order]`"
      >
      <input
        v-if="asset.group_id && !asset.willBeDeleted"
        type="hidden"
        :value="asset.group_id"
        :name="`existingMedia[${asset.id}][group_id]`"
      >
    </div>
  </div>

</template>

